import React, { useMemo } from 'react';
import classNames from 'classnames';
import ActiveFilterSortPills from 'components/ActiveFilterSortPills';
import MultiFilterPanel from 'components/MultiFilterPanel';
import MultiSortPanel from 'components/MultiSortPanel';
import NumberOfResultsPerPageFilter from 'components/NumberOfResultsPerPageFilter';
import useMultiSort from 'hooks/useMultiSort';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import useLeadsFilterOptions from './useLeadsFilterOptions';
import {
  HEADING_TRANSLATIONS,
  MULTI_SORT_DIRECTION_COPY,
  SORTING_VALUES,
} from '../../data';
import {
  INACTIVE_PAGE,
  ONGOING_PAGE,
} from '../../../../providers/Leads/Leads.provider';
import useChipLabelClickRefs from 'hooks/useChipLabelClickRefs';
import useQueryParams from 'hooks/useQueryParams';
import { getHasActiveFilters } from 'utils';

import './LeadsFilterControls.styles.scss';

type LeadsFilterControlsProps = {
  className?: string;
  columns: string[];
  sortParam?: string;
  filterParamPrefix?: string;
  pageParam?: string | string[];
  hasStageFilter?: boolean;
  isEmpty?: boolean;
  manageColumns?: React.ReactNode;
};

const LeadsFilterControls: React.FC<LeadsFilterControlsProps> = (props) => {
  const {
    className,
    columns,
    sortParam,
    filterParamPrefix,
    pageParam,
    hasStageFilter,
    isEmpty = false,
    manageColumns,
  } = props;

  const classes = classNames('leads-filter-controls', className);

  const { filterOptions } = useLeadsFilterOptions(
    filterParamPrefix,
    hasStageFilter,
  );

  const { limit, updateLimit } = useNumberOfResultsPerPage({
    pageParam: [ONGOING_PAGE, INACTIVE_PAGE],
  });

  const { multiSortValues, multiSortDirectionTranslation } = useMultiSort(
    columns,
    SORTING_VALUES,
    HEADING_TRANSLATIONS,
    MULTI_SORT_DIRECTION_COPY,
  );

  const {
    multiFilterRef,
    multiSortRef,
    onChipLabelClick,
    onSortChipLabelClick,
  } = useChipLabelClickRefs();

  const { params } = useQueryParams();

  const hasActiveFilters = useMemo(
    () => getHasActiveFilters(filterOptions, params),
    [filterOptions, params],
  );

  return (
    <div className={classes}>
      <ActiveFilterSortPills
        filterOptions={filterOptions}
        sortOptions={multiSortValues}
        sortParam={sortParam}
        onChipLabelClick={onChipLabelClick}
        onSortChipLabelClick={onSortChipLabelClick}
      />

      {(!isEmpty || hasActiveFilters) && (
        <div className="leads-filter-controls__actions">
          {!isEmpty && (
            <NumberOfResultsPerPageFilter
              limit={limit}
              updateLimit={updateLimit}
            />
          )}
          <MultiFilterPanel
            ref={multiFilterRef}
            filters={filterOptions}
            pageParam={pageParam}
          />
          <MultiSortPanel
            ref={multiSortRef}
            options={multiSortValues}
            sortDirectionTranslations={multiSortDirectionTranslation}
            sortParam={sortParam}
          />
          {manageColumns}
        </div>
      )}
    </div>
  );
};

export default LeadsFilterControls;
